<script setup lang="ts">
import type { PlpGridsMap } from '@design-system/components/Plp/FunctionalBar/PlpFunctionalBar.props'
import type { PlpPageProps } from './TemplatesPlp.props'
import type { PlpGridProps } from '@design-system/components/Plp/Grid/PlpGrid.props'
import type { BannerImage } from '@design-system/components/Plp/Hero/PlpHero.props'
import type { Middleware } from 'instantsearch.js'
import type {
  IndexWidget,
  StateMapping,
  Widget,
  InstantSearchOptions,
} from 'instantsearch.js'
import screens from '@design-system/configs/tailwind/screens'
import algoliasearch from 'algoliasearch'
import IconsGridFour from '~icons/icons/GridFour.svg'
import IconsGridMoodboard from '~icons/icons/GridMoodboard.svg'
import IconsGridOne from '~icons/icons/GridOne.svg'
import IconsGridThree from '~icons/icons/GridThree.svg'
import IconsGridTwo from '~icons/icons/GridTwo.svg'
import IconsGridWall from '~icons/icons/GridWall.svg'
import { SIDEBAR_IDS } from '@design-system/data/sidebarIds'
import { createFetchRequester } from '@algolia/requester-fetch'
import { useCustomAisRouter } from '@design-system/composables/useCustomAisRouter'
import { PLP_SELECTED_FOR_YOU_ID } from '@integration-layer/data/xGenIds'
import type { ProductCardHorizontal } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'

const props = defineProps<PlpPageProps>()
const data = computed(() => props)

const { currentWebsite } = useWebsite()
const { isShoppable } = await useIsShoppable()

const route = useRoute()
const app = useNuxtApp()
const hasEditorialContent = app.payload['has-plp-moodboard']

const { history } = useRouter().options
const { getCanonicalUrl } = useRouteHelper()

const handlePopState = () => {
  const backPlpRedirect = localStorage.getItem('backPlp')
  if (backPlpRedirect) {
    const path = getCanonicalUrl(`${window.location.origin}${backPlpRedirect}`)

    localStorage.removeItem('backPlp')
    window.location.href = path
  }
}

const { menuStatus } = useMenu()
const templatePlpState = useTemplatePlpState()
templatePlpState.value = data.value
const {
  title,
  bannerImage,
  subtitle,
  seoText,
  parentCategory,
  editorialContent,
} = useCategoryPage()
const { openQuickBuySlide } = useUIQuickBuyPopUpSlide()

const { y } = useScroll(window)
const { ts, getLocale } = useI18n()
const locale = getLocale()
const previousY = usePrevious(y)
const appConfig = useAppConfig()
const { dispatchSubMenuEvent } = useGANavigationEvents()
const currentWebsiteValue = currentWebsite.value

const searchClient = algoliasearch(
  useRuntimeConfig().public.algolia.applicationId,
  useRuntimeConfig().public.algolia.apiKey,
  { requester: createFetchRequester() }
)

const gridsMap: PlpGridsMap = {
  gridMoodboard: {
    name: 'gridMoodboard',
    icon: { sm: IconsGridMoodboard },
    class: 'grid-cols-12',
  },
  grid321: {
    name: 'grid321',
    icon: {
      sm: IconsGridOne,
      md: IconsGridTwo,
      lg: IconsGridThree,
    },
    class: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  },
  grid432: {
    name: 'grid432',
    icon: {
      sm: IconsGridTwo,
      md: IconsGridThree,
      lg: IconsGridFour,
    },
    class: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  },
  gridWall: {
    name: 'gridWall',
    icon: { sm: IconsGridWall, md: IconsGridWall },
    class: 'grid-cols-3 md:grid-cols-5 lg:grid-cols-6',
  },
} as const
if (currentWebsiteValue === 'giorgio-armani') {
  gridsMap.grid321 = {
    name: 'grid321',
    icon: {
      sm: IconsGridTwo,
      md: IconsGridTwo,
      lg: IconsGridThree,
    },
    class: 'grid-cols-2 md:grid-cols-2 lg:grid-cols-3',
  }

  gridsMap.grid432 = {
    name: 'grid432',
    icon: {
      sm: IconsGridOne,
      md: IconsGridThree,
      lg: IconsGridFour,
    },
    class: 'grid-cols-1 md:grid-cols-3 lg:grid-cols-4',
  }
}
const gridLayoutCookie = useCookie<GridConfig | undefined>(
  `${currentWebsite.value}-grid-layout-cookies`
)
const defaultGridName =
  currentWebsiteValue === 'giorgio-armani' ? 'grid321' : 'grid432'
const gridName = gridLayoutCookie.value?.name || defaultGridName

const gridConfiguration = ref(gridsMap[gridName])

const indexName = computed(() => `${appConfig.currentAlgoliaIndex}`)

interface GridConfig {
  name: keyof typeof gridsMap
}

const nActiveFilters = ref(6)

const sidePlpFilters = SIDEBAR_IDS.plpFiltersAside

const { closeDialog, isDialogOpen, openDialog } = useDialog(sidePlpFilters)

const hitsPerPage = computed(() => {
  return width.value ? (width.value > parseFloat(screens.md) ? 36 : 24) : 36
})
const SIZE_FILTERS_ID = 'size-filters'
const COLOR_FILTERS_ID = 'color-filters'

const openFilter = ref('')

const openQuickBuyPopUpLayer = (product: ProductCardHorizontal) => {
  openQuickBuySlide({
    product,
    isQuickBuyPlp: true,
  })
}

const { width, transformRefinements, transformSubCategory } =
  await useTemplatePlp()

const { breadcrumbs } = useBreadcrumbs({
  dataOrigin: 'amplience',
  currentPageTitle: title.value,
  pageData: parentCategory.value,
})

const algoliaBrandFilter = computed(
  () => `categoryPageId2: "${props.categoryId}" AND FullPrice>0`
)

const currentAlgoliaIndex = useAppConfig().currentAlgoliaIndex

const defaultIndex = ref({
  value: `${currentAlgoliaIndex}`,
  label: ts('plp.filters.recommended'),
})

const sortingItems = ref([
  {
    value: `${currentAlgoliaIndex}`,
    label: ts('plp.filters.recommended'),
  },
  {
    value: `dynamic_listing-${currentAlgoliaIndex}`,
    label: ts('plp.filters.recommended'),
  },
  {
    value: `price_asc-${currentAlgoliaIndex}`,
    label: ts('plp.filters.priceLow'),
  },
  {
    value: `price_desc-${currentAlgoliaIndex}`,
    label: ts('plp.filters.priceHigh'),
  },
])

const hasFilters = ref(false)

watch(
  () => route.query,
  newQuery => {
    if (newQuery.filter && !hasEditorialContent) {
      hasFilters.value = true
    } else {
      hasFilters.value = false
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

const updateModelSorting = (
  value: string | undefined,
  refine: (value: string) => void
) => {
  if (value) {
    refine(value)
  } else {
    refine(defaultIndex.value.value)
  }
}

const sortingColors = <Item extends { name: string }>(a: Item, b: Item) => {
  if (!a.name.split('_')[1] || !b.name.split('_')[1]) return 0
  return a.name.split('_')[1].localeCompare(b.name.split('_')[1])
}

const refinementsState = useState('ais_refinement_render_state')

const storageStage = useState('_instantsearchStorageState')

const activeMicroCatLength = ref(0)
const macroCatLength = ref(0)

const { skuPrices, fetchSkuPricesAlgoliaMiddleware } = useProductTileClPrices()

// instant search configuration
const widgets = ref<(Widget | IndexWidget)[]>([
  useAisStats({}),
  useAisInfiniteHits({
    showPrevious: true,
    cache: useAisInfiniteHitsStatefulCache('index'),
  }),
  useAisCurrentRefinements({
    //@ts-ignore
    transformItems: transformRefinements,
  }),
  useAisClearRefinements({}),
  useCustomSortBy({
    items: sortingItems.value,
  }),
  useAisRefinementList({
    attribute: 'highlighted',
    showMore: true,
    limit: 5,
  }),
  useAisRefinementList({
    attribute: 'Gender',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: `macroCat`,
    showMore: true,
    showMoreLimit: 100,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: `microCat`,
    showMore: false,
    limit: 100,
    sortBy: ['name:asc'],
    //@ts-ignore
    transformItems: transformSubCategory,
  }),
  useAisRefinementList({
    attribute: 'categoryPageId.name',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: 'MacroColorDesc',
    showMore: true,
    limit: 6,
    sortBy: (a, b) => sortingColors(a, b),
  }),
  useAisRefinementList({
    attribute: 'size.sizeCode',
    showMore: false,
    limit: 100,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: 'attributes.Occasion',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: 'attributes.Sport',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: 'attributes.Denim_Fit',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: 'attributes.Technology',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisRefinementList({
    attribute: 'attributes.Sneakers',
    showMore: true,
    limit: 5,
    sortBy: ['name:asc'],
  }),
  useAisConfigure({
    searchParameters: {
      //@ts-ignore - consiglio di matte
      hitsPerPage: hitsPerPage.value,
      distinct: true,
      filters: algoliaBrandFilter.value,
      clickAnalytics: true,
    },
  }),
])

const middlewares: Middleware[] = [
  ({ instantSearchInstance }) => {
    const listener = async () => {
      await nextTick()
      await fetchSkuPricesAlgoliaMiddleware(instantSearchInstance)
    }
    return {
      async subscribe() {
        await fetchSkuPricesAlgoliaMiddleware(instantSearchInstance)
        instantSearchInstance.mainHelper?.on('searchQueueEmpty', listener)
      },
      unsubscribe() {
        instantSearchInstance.removeListener('searchQueueEmpty', listener)
      },
    }
  },
]

const router = useCustomAisRouter()
const routing = computed(() => ({
  router: router.value.router!,
  stateMapping: {
    stateToRoute(uiState: any) {
      const indexCurrentName = `${currentAlgoliaIndex}`
      const indexUiState = uiState[indexCurrentName]

      return {
        ...route.query,
        order:
          indexUiState?.sortBy?.indexOf('dynamic') !== -1
            ? undefined
            : indexUiState.sortBy,
        filter: indexUiState && indexUiState?.refinementList,
        page: indexUiState && indexUiState?.page,
      }
    },
    routeToState(routeState: any) {
      const indexRouteState = `${currentAlgoliaIndex}`

      const hasOrder = routeState?.order !== undefined
      const isMoodboard = gridLayoutCookie.value?.name === 'gridMoodboard'
      return {
        [indexRouteState]: {
          sortBy:
            hasEditorialContent && !hasOrder && isMoodboard
              ? 'dynamic_listing-' + currentAlgoliaIndex
              : routeState?.order,
          refinementList: routeState?.filter,
          page: routeState?.page,
        },
      }
    },
  } as StateMapping,
}))

const configuration = computed<InstantSearchOptions>(() => ({
  indexName: indexName.value,
  searchClient,
  routing: routing.value,
  insights: true,
}))

const changeColumnHandler = (event: PlpGridProps['gridConfiguration']) => {
  gridConfiguration.value = event
}

const scrollToId = (id: string) => {
  const element = document.getElementById(id)
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
    })
  }
}

const onShowResultsClick = () => {
  closeDialog(sidePlpFilters)
  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
}

watch(isDialogOpen, newOpenFilterValue => {
  if (newOpenFilterValue && openFilter.value !== '') {
    setTimeout(() => {
      scrollToId(openFilter.value)
      openFilter.value = ''
    }, 400)
  }
})

// the setTimeout is needed to wait for the dialog to open and wait all the transitions to finish to open the accordin selected
const openFilterDialog = (filter: string) => {
  openDialog(sidePlpFilters)
  openFilter.value = filter
}

onMounted(() => {
  const scrollYVal = localStorage.getItem('scrollYVal')
    ? JSON.parse(localStorage.getItem('scrollYVal') || '')
    : 0
  scrollIntoPageView({
    scrollBothStrips: false,
    dynamicScrollPlp: scrollYVal.prodPage,
  })
  localStorage.setItem(
    'scrollYVal',
    JSON.stringify({
      scroll: scrollYVal.scroll ?? '',
      prodPage: false,
    })
  )

  activeMicroCatLength.value =
    //@ts-ignore
    storageStage?.value?.index.state.disjunctiveFacetsRefinements.microCat.length

  macroCatLength.value =
    //@ts-ignore
    refinementsState?.value?.macroCat.items.length

  //need to manage back redirect on previous page
  localStorage.setItem('backPlp', history.state.back as string)
  window.addEventListener('popstate', handlePopState)
})

onUnmounted(() => {
  localStorage.removeItem('backPlp')
  window.removeEventListener('popstate', handlePopState)
})

if (
  gridLayoutCookie.value?.name === 'gridMoodboard' &&
  editorialContent?.value === undefined
) {
  gridConfiguration.value =
    gridsMap[currentWebsiteValue === 'giorgio-armani' ? 'grid321' : 'grid432']
} else if (editorialContent?.value?.length ?? 0) {
  gridConfiguration.value =
    gridsMap[gridLayoutCookie.value?.name ?? 'gridMoodboard']
} else {
  const defaultGridLayout =
    currentWebsiteValue === 'giorgio-armani' ? 'grid321' : 'grid432'

  gridConfiguration.value =
    gridsMap[gridLayoutCookie.value?.name ?? defaultGridLayout]
}

const scrollDownCheck = computed(
  () => previousY.value !== undefined && y.value > previousY.value
)

const isVisualFilterEnabled = computed(() => {
  const { visualFilter } = props
  return (
    visualFilter &&
    visualFilter.enable &&
    visualFilter.facet &&
    ((visualFilter.type === 'media' && visualFilter.values.length) ||
      visualFilter.type)
  )
})

const { category } = await useCrossNavigationsLinks(
  props.brand!,
  route.fullPath
)

const printLabel = (refinement: any) => {
  return refinement.label.includes('_')
    ? refinement.label.split('_').at(-1)
    : refinement.label
}
</script>

<template>
  <div>
    <PlpHero
      v-if="bannerImage"
      :category-name="title"
      :subtitle="subtitle"
      :image="bannerImage as unknown as BannerImage"
    />
    <PlpCrossNavigationsLinks
      v-else-if="category"
      v-bind="category"
      :enable-heading="!bannerImage"
      @on-link-click="dispatchSubMenuEvent"
    />
    <div>
      <AisInstantSearch
        :widgets="widgets"
        :configuration="configuration"
        :middlewares="middlewares"
        instance-key="plp"
      >
        <AlgoliaVisualFilters
          v-if="visualFilter && isVisualFilterEnabled"
          v-bind="visualFilter"
        />
        <AlgoliaHandleDynamicListingSorting
          :grid-configuration="gridConfiguration.name"
        />

        <AisStats
          class="sticky left-0 top-[calc(var(--header-default-height)_-_1px)] z-50 transition-all"
          :class="{
            'z-auto': menuStatus.isActive,
            '!top-0': scrollDownCheck,
          }"
        >
          <template #default="{ nbHits }">
            <PlpFunctionalBar
              :items="nbHits"
              :editorial-content="editorialContent"
              :grids-map="gridsMap"
              @grid-layout="changeColumnHandler"
              @open-filter="openFilterDialog($event)"
            />
          </template>
        </AisStats>
        <AlgoliaAisInfiniteHits
          :grid-configuration="gridConfiguration"
          :editorial-content="editorialContent"
          :has-filters="hasFilters"
          :product-prices="skuPrices"
          :x-gen-first-row-component="xGenFirstRowComponent"
        />
        <OrganismsSidebarSlide
          :id="sidePlpFilters"
          :header-props="{
            titleText: $ts('plp.filtersSideSlide.title'),
            containerClass: 'pb-lg md:pb-xl',
          }"
        >
          <template #body>
            <AisCurrentRefinements>
              <template #default="{ items }">
                <div
                  v-if="items.length > 0"
                  class="mb-4 flex items-center justify-between"
                >
                  <span class="text-book-6">
                    {{ $ts('plp.filtersSideSlide.appliedFilters') }}
                  </span>
                  <AlgoliaClearRefinements />
                </div>
                <div :class="{ 'mb-8': items.length > 0 }">
                  <div class="gap-xs flex flex-wrap">
                    <div
                      v-for="item in items.slice(0, nActiveFilters)"
                      :key="item.label"
                    >
                      <div
                        v-for="refinement in item.refinements"
                        :key="`${refinement.attribute}-${refinement.value}`"
                        class="bg-primitives-off-white text-primitives-black flex w-fit items-center px-4 py-2"
                      >
                        <button
                          class="mr-1"
                          :aria-label="`${$ts('accessibility.removeFilter')} ${printLabel(refinement)}`"
                          @click.prevent="item.refine(refinement)"
                        >
                          <DefaultIconsClose
                            aria-hidden="true"
                            class="h-4 w-4"
                          />
                        </button>
                        <div class="flex gap-3">
                          <div
                            v-if="
                              refinement.label.includes('_') &&
                              refinement.attribute === 'MacroColorDesc'
                            "
                            :style="{
                              'background-color': `#${refinement.label.split('_')[0]}`,
                            }"
                            class="h-4 w-4"
                          />
                          <span class="text-book-7">
                            {{ printLabel(refinement) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <AtomsCta
                    v-if="items.length > 6 && nActiveFilters !== items.length"
                    anatomy="link"
                    class="mt-4"
                    @click="nActiveFilters = items.length"
                  >
                    <template #label>
                      {{
                        $ts('plp.filtersSideSlide.showMore', {
                          n: items.length - 6,
                        })
                      }}
                    </template>
                  </AtomsCta>
                  <AtomsCta
                    v-if="nActiveFilters === items.length && items.length !== 6"
                    anatomy="link"
                    class="mt-4"
                    @click="nActiveFilters = 6"
                  >
                    <template #label>
                      {{ $ts('plp.showLess') }}
                    </template>
                  </AtomsCta>
                </div>
              </template>
            </AisCurrentRefinements>

            <div class="border-primitives-grey-100 border-t">
              <UIDropdown
                :title="$ts('plp.filters.sortBy')"
                default-open
                :custom-title-style="'text-book-5'"
                title-wrapper="h3"
              >
                <div class="mb-6 flex flex-col gap-6 font-light">
                  <div>
                    <AisSortBy>
                      <template #default="{ items, refine, currentRefinement }">
                        <InputsRadioWithSlot
                          v-for="(option, index) in items"
                          v-show="
                            index !==
                              (gridConfiguration.name === 'gridMoodboard'
                                ? 0
                                : 1) &&
                            !(option.label.includes('Price') && !isShoppable)
                          "
                          :id="`option-${option.label}`"
                          :key="`option-${option.label}`"
                          :model-value="
                            currentRefinement === defaultIndex.value
                              ? defaultIndex.value
                              : currentRefinement
                          "
                          :label="option.label"
                          name="sortBy"
                          :value="option.value"
                          :custom-class="'bg-transparent !py-1 !mb-1'"
                          class="px-0"
                          is-select-size-component
                          @update:model-value="
                            value =>
                              updateModelSorting(
                                value as string | undefined,
                                refine
                              )
                          "
                        />
                      </template>
                    </AisSortBy>
                  </div>
                </div>
              </UIDropdown>
            </div>

            <AlgoliaDropdownFilters
              id="highlighted-filters"
              :title="$ts('plp.filters.highlighted')"
              attribute="highlighted"
              :default-open="true"
              border
            />

            <AlgoliaDropdownFilters
              id="gender-filters"
              :title="$ts('plp.filters.gender')"
              attribute="Gender"
              border
            />

            <AlgoliaCategoryFilters
              v-if="macroCatLength > 1 || activeMicroCatLength > 0"
              id="category-filters"
              :title="$ts('plp.filters.category')"
              attribute="macroCat"
              :type="`mainCategory`"
              border
            />

            <AlgoliaCategoryFilters
              v-else
              id="category-filters"
              :title="$ts('plp.filters.category')"
              attribute="microCat"
              :type="`mainCategory`"
              border
            />

            <AlgoliaAccordionFilters
              v-if="macroCatLength > 1 || activeMicroCatLength > 0"
              id="subcategory-filters"
              :title="$ts('plp.filters.subcategory')"
              :attribute="`microCat`"
              :type="`subCategory`"
              border
            />

            <AlgoliaDropdownFilters
              :id="COLOR_FILTERS_ID"
              :title="$ts('plp.filters.color')"
              attribute="MacroColorDesc"
              :default-open="openFilter === COLOR_FILTERS_ID"
              border
            />
            <AlgoliaDropdownFilters
              v-if="isShoppable"
              :id="SIZE_FILTERS_ID"
              :title="$ts('plp.filters.size')"
              attribute="size.sizeCode"
              :default-open="openFilter === SIZE_FILTERS_ID"
              border
            />

            <AlgoliaDropdownFilters
              id="occasion-filters"
              :title="$ts('plp.filters.sale')"
              attribute="attributes.Occasion"
              border
            />

            <AlgoliaDropdownFilters
              id="sport-filters"
              :title="$ts('plp.filters.sport')"
              attribute="attributes.Sport"
              border
            />

            <AlgoliaDropdownFilters
              id="denim-filters"
              :title="$ts('plp.filters.denimFit')"
              attribute="attributes.Denim_Fit"
              border
            />

            <AlgoliaDropdownFilters
              id="technology-filters"
              :title="$ts('plp.filters.technology')"
              attribute="attributes.Technology"
              border
            />

            <AlgoliaDropdownFilters
              id="sneakers-filters"
              :title="$ts('plp.filters.sneakers')"
              attribute="attributes.Sneakers"
              border
            />
          </template>
          <template #footer>
            <div
              class="bg-primitives-white positive-padding z-10 flex items-center justify-center py-6"
            >
              <AisStats class="w-full">
                <template #default="{ nbHits }">
                  <AtomsCta
                    :icon-show-default="false"
                    :full-width="true"
                    @click-handler="onShowResultsClick"
                  >
                    <template #label>
                      {{
                        $ts('plp.filtersSideSlide.seeResults', {
                          n: nbHits,
                        })
                      }}
                    </template>
                  </AtomsCta>
                </template>
              </AisStats>
            </div>
          </template>
        </OrganismsSidebarSlide>
      </AisInstantSearch>
    </div>

    <DelayHydration>
      <LazyXGenRecommendations
        :prediction="PLP_SELECTED_FOR_YOU_ID"
        :cache-x-gen-response="false"
      >
        <template #default="{ products, status }">
          <LazyPdpRelatedProducts
            v-if="status !== 'pending' && products && products.length"
            id="youMayAlsoLike"
            :title="$ts('myAccount.logged.overviewContent.selectedForYou')"
            :initial-cl-fetch="10"
          >
            <UISliderWithArrows
              :id="`XSE-${PLP_SELECTED_FOR_YOU_ID}`"
              :items="products"
              class="XGen_SmartElement"
              has-slide-vertical-border
              slide-custom-width="w-[calc((100%_-_var(--padding-x)_*_2)_/_2)] lg:w-[calc((100%_-_var(--padding-x)_*_2)_/_5)]"
            >
              <template #default="item">
                <WrapperProductTile :product="item">
                  <template #default="{ product, productCardHorizontal }">
                    <UIProductTile
                      xse-type="rec"
                      :xse-prod-code="product.sku"
                      :disable-swipe-mobile="true"
                      :product="{
                        ...product,
                        linkToProduct: `${product.linkToProduct}?xse_prod_code=${product.sku}&xse=${PLP_SELECTED_FOR_YOU_ID}`,
                      }"
                      @open-quick-buy="
                        () =>
                          productCardHorizontal &&
                          openQuickBuyPopUpLayer(productCardHorizontal)
                      "
                    />
                  </template>
                </WrapperProductTile>
              </template>
            </UISliderWithArrows>
          </LazyPdpRelatedProducts>
        </template>
      </LazyXGenRecommendations>
    </DelayHydration>

    <EditorialCrossNavigation v-if="crossNavigation" v-bind="crossNavigation" />

    <PlpSeoText :seo-text="seoText" :tag="'p'" />
    <UIBreadcrumbs
      v-if="breadcrumbs"
      class="h-17 text-text-primary bg-background-secondary positive-padding py-6 md:py-6 lg:h-16 lg:py-6"
      :items="breadcrumbs"
    />

    <div class="editorial-modules-wrapper">
      <EditorialModulesWrapper
        :delivery-key="`${currentWebsite}-service-strip`"
      />
    </div>
  </div>
</template>
